<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { FilterMatchMode } from '@primevue/core/api';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import { useDataTableStore } from '@stores/useDataTableStore';
import { trans } from '../../../_00_elements/translation';
import SubscriptionDetails from './components/SubscriptionDetails.vue';
import SubscriptionRelatedBuyer from './components/SubscriptionRelatedBuyer.vue';
import SubscriptionRelatedOfficeOffer from './components/SubscriptionRelatedOfficeOffer.vue';
import SubscriptionMobileDetails from './components/SubscriptionMobileDetails.vue';
import SubscriptionMenu from './menu/SubscriptionMenu.vue';
import { PAY_CLIENT_BILLING_DOCUMENTS_ENDPOINT } from '../../../_00_elements/tools/routes/routes';
import InvoiceTable from '../invoices/InvoiceDataTable.vue';
import InvoiceDataList from '../invoices/InvoiceDataList.vue';
import { SUBSCRIPTION_STATUS } from '../constants/constants';

const subscriptionStore = useSubscriptionStore();
const { isMobile } = storeToRefs(useScreensizeStore());
const subscriptionData = computed(() => subscriptionStore.selectedSubscriptionData);

const route = useRoute();
const router = useRouter();
const { id } = route.params;

const datatableStore = useDataTableStore();
const { addFilter, setEndpoint, setDefaultOrder } = datatableStore;

setEndpoint(PAY_CLIENT_BILLING_DOCUMENTS_ENDPOINT);
setDefaultOrder('billingDocument.createdAt', 'desc');
addFilter('subscription', id, FilterMatchMode.EQUALS, false);

const goBack = () => {
  subscriptionStore.setSelectedData(null);
  router.push({ name: 'subscriptions' });
};

onMounted(async () => {
  if (!subscriptionData.value) {
    await subscriptionStore.getSubscription(id);
  }

  document.title = trans('gestion.subscription.page.title', {
    name: subscriptionData?.value?.title,
  });
});

onBeforeUnmount(() => datatableStore.reset());
</script>

<template>
  <div v-if="subscriptionData" class="subscription-details">
    <span class="subscription-details__back" @click="goBack">
      {{ trans('gestion.subscription.title') }}
    </span>
    <div class="subscription-details__title-block">
      <h3 class="subscription-details__title">
        {{ subscriptionData.title }}
      </h3>
      <SubscriptionMenu
        v-if="subscriptionData.status !== SUBSCRIPTION_STATUS.TERMINATED"
        :subscription="subscriptionData"
      />
    </div>
    <div class="subscription-details__content">
      <div class="subscription-details__customer-info">
        <div class="subscription-details__customer-title subscription-details__customer-title--higher">
          {{ trans('gestion.subscription.details') }}
        </div>
        <template v-if="isMobile">
          <SubscriptionMobileDetails />
        </template>
        <template v-else>
          <SubscriptionDetails />
          <SubscriptionRelatedBuyer />
          <SubscriptionRelatedOfficeOffer v-if="subscriptionData.address || subscriptionData.office_offers?.length > 0" />
        </template>
      </div>
      <div class="subscription-details__subscription-info">
        <div class="subscription-details__customer-title">
          {{ trans('gestion.subscription.invoices.title') }}
        </div>

        <InvoiceDataList v-if="isMobile" />
        <InvoiceTable v-else :hidden-columns="[ 'client', 'seller' ]" />
      </div>
    </div>
  </div>
</template>
